// Lifted from mobile LoggerScreen (ALIGNMENT_AUDIT HIGH 2, now next/archive/ALIGNMENT_AUDIT.md).
// Web should adopt over time — today it surfaces save errors
// inconsistently because it has no equivalent of these helpers.
//
// The signatures take `unknown` so callers don't have to narrow before
// invoking. Each surface owns its own `ApiError` class (mobile in
// `apps/mobile/src/api/index.ts`, web in `apps/web-rnw/src/api.ts`); to
// stay surface-agnostic we duck-type on the shape both classes share:
// `{ status: number; body: unknown }` plus a string `message`. That's
// stable because both classes hand-roll the same `Error` extension.

/**
 * The shape every surface's `ApiError` class shares. Exported so shared
 * hooks can expose a typed `.status` without importing any one surface's
 * class — `useAsyncList` (packages/ui) is the first such consumer.
 */
export type ApiErrorLike = {
  readonly status: number;
  readonly body: unknown;
  readonly message: string;
};

export const isApiErrorLike = (err: unknown): err is ApiErrorLike => {
  if (!(err instanceof Error)) return false;
  const candidate = err as Error & { status?: unknown; body?: unknown };
  return typeof candidate.status === "number" && "body" in candidate;
};

/** The code of the request that never left: the device knew it was offline
 *  (#397). Status 0 like `api_timeout`: no server answered, so it is never a
 *  verdict. */
export const API_OFFLINE = "api_offline";

export const isOfflineError = (err: unknown): boolean =>
  isApiErrorLike(err) && err.status === 0 && err.message === API_OFFLINE;

/** The code a read hook keeps for a thrown error: an ApiError's message
 *  (`api_error_500`, `api_offline`, ...). */
export const errorCodeOf = (err: unknown): string =>
  err instanceof Error ? err.message : "load_failed";

/**
 * Tells a network-layer failure (no response) apart from a server-side
 * failure (HTTP non-2xx). An `ApiError` (duck-typed) means the server
 * replied, except `API_OFFLINE`, which never left. Anything else (`TypeError`
 * from `fetch`, abort errors, timeout messages) is a transport problem and the caller should
 * enqueue/retry rather than display a server error slug.
 */
export const isNetworkError = (err: unknown): boolean => {
  if (isApiErrorLike(err)) return isOfflineError(err);
  if (err instanceof TypeError) return true;
  const msg = err instanceof Error ? err.message : String(err);
  return /network|fetch|timeout|connection/i.test(msg);
};

/**
 * The server's `error` slug alone (`item_gone`, `not_pending`, …), for
 * callers that BRANCH on it rather than display it. `undefined` when there is
 * none to read: a transport error, or a body without a string `error`.
 */
export const apiErrorSlug = (err: unknown): string | undefined => {
  if (!isApiErrorLike(err)) return undefined;
  const body = err.body;
  if (!body || typeof body !== "object") return undefined;
  const slug = (body as { error?: unknown }).error;
  return typeof slug === "string" ? slug : undefined;
};

/**
 * Surface the most informative slice of an `ApiError` body so the user
 * can act on it: the server's `error` slug for known problems
 * (`invalid_payload`, `invalid_idempotency_key`, `duplicate_id`,
 * `forbidden`, …), plus any first-issue path/message from a Zod
 * validation failure. Falls back to the HTTP status code, then to the
 * stringified error if the input isn't an `ApiError` at all.
 */
export const apiErrorSummary = (err: unknown): string => {
  if (!isApiErrorLike(err)) {
    if (err instanceof Error) return err.message;
    return String(err);
  }
  const body = err.body;
  if (body && typeof body === "object") {
    const errSlug = apiErrorSlug(err);
    const issues = (body as { issues?: unknown }).issues;
    const issueLine =
      Array.isArray(issues) && issues.length > 0
        ? (() => {
            const first = issues[0] as {
              path?: unknown;
              message?: unknown;
            };
            const p = Array.isArray(first.path) ? first.path.join(".") : "";
            const m = typeof first.message === "string" ? first.message : "";
            return p || m ? `${p}${p && m ? ": " : ""}${m}` : "";
          })()
        : "";
    if (typeof errSlug === "string") {
      return issueLine ? `${errSlug} (${issueLine})` : errSlug;
    }
  }
  return `HTTP ${err.status}`;
};

/**
 * A verdict the CLIENT reaches BEFORE any request leaves the device: the
 * payload carries more tags than the schema allows, and no replay can fix
 * it. It is the local twin of a deterministic 4xx — the save flow must
 * surface it inline, never hand it to the offline outbox (CN-TAGCAP-THROW).
 *
 * Thrown by `splitWorkout`. Before this type it was a plain `Error`, so on
 * mobile it fell through `dispositionOf`'s `!isApiError → 'retry'` default,
 * got queued, burned `MAX_ATTEMPTS` and stranded as `permanent_fail`; the
 * athlete saw "queued" then "failed", never which entry or why. Web-rnw
 * already surfaced it (its `shouldQueueError` is `() => false`).
 *
 * The guard duck-types on `name`, not `instanceof`, so it survives a
 * duplicated module instance in the RN bundle — the same robustness reason
 * `apiErrorSummary` above duck-types on shape.
 */
export class TagCapError extends Error {
  readonly entryIdx: number;
  readonly tagCount: number;
  readonly cap: number;
  constructor(entryIdx: number, tagCount: number, cap: number) {
    super(
      `entry ${entryIdx} produced ${tagCount} tags (cap ${cap}). ` +
        `Reduce the tags on this exercise before saving.`,
    );
    this.name = "TagCapError";
    this.entryIdx = entryIdx;
    this.tagCount = tagCount;
    this.cap = cap;
  }
}

export const isTagCapError = (err: unknown): err is TagCapError =>
  err instanceof Error && err.name === "TagCapError";
